<template>
    <v-sheet>
        <div class="text-subtitle-1 font-weight-bold text-center">
            All Operations
        </div>

        <v-data-table
            density="compact"
            color="primary"
            :fixed-header="true"
            :headers="tableHeaders"
            :items="filteredData"
        >
            <template
                v-slot:headers="{ columns, isSorted, getSortIcon, toggleSort }"
            >
                <tr class="table-head">
                    <template v-for="column in columns" :key="column.key">
                        <th class="text-no-wrap bg-primary">
                            <div
                                @mouseover="hoverColumn = column.key"
                                @mouseleave="hoverColumn = null"
                                class="d-flex align-center"
                            >
                                <!-- Filter Menu -->
                                <v-menu
                                    offset-y
                                    :close-on-content-click="false"
                                    scroll-strategy="close"
                                    v-model="showFilter[column.key]"
                                >
                                    <template v-slot:activator="{ props }">
                                        <v-icon
                                            v-bind="props"
                                            icon="mdi:mdi-filter"
                                            size="small"
                                            class="mr-1"
                                            :color="
                                                selectedFilters[column.key]
                                                    ? 'green'
                                                    : 'white'
                                            "
                                            v-if="
                                                filterOptions[column.key] &&
                                                filterOptions[column.key].length
                                            "
                                        />
                                    </template>

                                    <v-card
                                        class="pa-2"
                                        style="background-color: white"
                                    >
                                        <v-autocomplete
                                            v-model="
                                                selectedFilters[column.key]
                                            "
                                            :items="
                                                filterOptions[column.key] || []
                                            "
                                            variant="underlined"
                                            :label="`${column.title} Filter`"
                                            class="mx-4"
                                            :clearable="true"
                                            hide-details
                                            @update:modelValue="
                                                filterItems(column.key)
                                            "
                                            style="
                                                min-width: 225px;
                                                background-color: white;
                                            "
                                        />
                                        <div
                                            class="d-flex justify-center align-center mt-2"
                                        >
                                            <v-btn
                                                variant="outlined"
                                                density="compact"
                                                @click="
                                                    showFilter[
                                                        column.key
                                                    ] = false
                                                "
                                            >
                                                Close
                                            </v-btn>
                                        </div>
                                    </v-card>
                                </v-menu>

                                <!-- Column Title & Sort Icon -->
                                <span
                                    v-if="column.sortable"
                                    class="cursor-pointer"
                                    @click="() => toggleSort(column)"
                                >
                                    <b>{{ column.title }}</b>
                                    <v-icon
                                        v-if="
                                            isSorted(column) ||
                                            hoverColumn === column.key
                                        "
                                        :icon="getSortIcon(column)"
                                        size="0.875rem"
                                        class="ml-1"
                                        @click="() => toggleSort(column)"
                                    ></v-icon>
                                </span>
                                <span v-else>
                                    <b>{{ column.title }}</b>
                                </span>
                            </div>
                        </th>
                    </template>
                </tr>
            </template>
            <template v-slot:item.Actions="{ item }">
                <div class="text-no-wrap">
                    <v-btn
                        size="small"
                        variant="text"
                        prepend-icon="mdi:mdi-pencil"
                        @click="openScrapModel(item)"
                    >
                        <v-tooltip activator="parent">Edit Scrap</v-tooltip>
                    </v-btn>
                </div>
            </template>
            <template v-slot:item.PartNum="{ item }">
                <div class="text-no-wrap">{{ item.job_asmbl.PartNum }}</div>
            </template>
            <template v-slot:item.AssemblySeq="{ item }">
                <div class="text-no-wrap">{{ item.job_asmbl.AssemblySeq }}</div>
            </template>
            <template v-slot:item.OpDesc="{ value }">
                <div class="text-truncate" style="max-width: 200px">
                    {{ value }}
                    <v-tooltip activator="parent" location="bottom">
                        {{ value }}
                    </v-tooltip>
                </div>
            </template>
            <template v-slot:item.EstScrap="{ item }">
                <div class="text-no-wrap">
                    {{ Number(item.EstScrap) }}{{ item.EstScrapType }}
                </div>
            </template>
        </v-data-table>

        <v-dialog v-model="editScrapModal" :retain-focus="false">
            <v-card
                class="mx-auto"
                variant="flat"
                title="Edit Scrap Qty For Operation"
                width="65%"
            >
                <v-container fluid>
                    <v-row>
                        <v-col cols="4">
                            <v-text-field
                                v-model="editedOpr.AssemblySeq"
                                readonly
                                variant="underlined"
                                label="AssemblySeq"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="4">
                            <v-text-field
                                v-model="editedOpr.OpDesc"
                                readonly
                                variant="underlined"
                                label="Operation"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="4">
                            <v-text-field
                                v-model="editedOpr.job_asmbl.PartNum"
                                readonly
                                variant="underlined"
                                label="PartNum"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="4">
                            <v-text-field
                                v-model="editedOpr.EstScrap"
                                label="Scrap"
                                variant="outlined"
                                density="compact"
                                color="primary"
                                type="number"
                                min="0"
                                placeholder="0.00"
                                hide-details
                                :readonly="parseInt(job?.JobClosed) !== 0"
                                :disabled="loading"
                            ></v-text-field>
                        </v-col>

                        <v-col cols="3">
                            <v-radio-group
                                :inline="true"
                                v-model="editedOpr.EstScrapType"
                                :disabled="loading"
                            >
                                <v-radio label="Qty" value="Q"></v-radio>

                                <v-radio label="%" value="%"></v-radio>
                            </v-radio-group>
                        </v-col>
                        <v-col cols="5">
                            <v-checkbox
                                :disabled="loading"
                                v-model="applyToAll"
                                :label="`Apply Scrap Setting to All
                                        ${editedOpr.OpDesc} Operations?`"
                            >
                            </v-checkbox>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="6">
                            <v-btn
                                color="close"
                                block
                                @click="closeScrapModel"
                                :disabled="loading"
                                >Close</v-btn
                            >
                        </v-col>
                        <v-col cols="6">
                            <v-btn
                                :loading="loading"
                                color="primary"
                                block
                                @click="submit"
                                >Save</v-btn
                            >
                        </v-col>
                    </v-row>
                </v-container>
            </v-card>
        </v-dialog>
    </v-sheet>
</template>

<script>
import { reactive, ref, computed, onMounted, watch } from "vue";
import { useJobEntryStore } from "../../../../../../stores/ProjectManagement/JobEntry";

export default {
    name: "ListWindow",
    setup() {
        const jobEntryStore = useJobEntryStore();
        const job = computed(() => jobEntryStore.job);
        const operationsList = computed(
            () => jobEntryStore.allOperations ?? []
        );

        const { refreshAssemblies, setErrorMessage, setTemporaryMessage } =
            jobEntryStore;

        const loading = ref(false);
        const editScrapModal = ref(false);
        const editedOpr = ref({});
        const origOpr = ref({});
        const applyToAll = ref(false);
        const hoverColumn = ref(null);

        // --- FILTER SETUP ---
        const filterKeys = ["OpCode"]; // Add more filterable keys here
        const filterOptions = reactive({});
        const selectedFilters = reactive({});
        const showFilter = reactive({});

        // Initialize reactive filter objects
        filterKeys.forEach((key) => {
            filterOptions[key] = [];
            selectedFilters[key] = null;
            showFilter[key] = false;
        });

        const filteredData = ref([]);

        // --- TABLE HEADERS ---
        const tableHeaders = ref([
            { title: "", key: "Actions" },
            { title: "ASM", key: "AssemblySeq" },
            { title: "PartNum", key: "PartNum" },
            { title: "Opr.", key: "OprSeq" },
            { title: "Operation Description", key: "OpDesc" },
            { title: "Operation", key: "OpCode" },
            { title: "ProductionQty", key: "RunQty" },
            { title: "Qty/Parent", key: "QtyPer" },
            { title: "Est. Scrap", key: "EstScrap" },
        ]);

        // --- FILTER FUNCTIONS ---
        function setFilterOptions() {
            filterKeys.forEach((key) => (filterOptions[key] = []));
            operationsList.value.forEach((item) => {
                filterKeys.forEach((key) => {
                    const value = item[key] ?? item.job_asmbl?.[key];
                    if (value != null && !filterOptions[key].includes(value)) {
                        filterOptions[key].push(value);
                    }
                });
            });

            // Sort arrays
            filterKeys.forEach((key) => {
                filterOptions[key].sort((a, b) =>
                    a.toString().localeCompare(b.toString(), undefined, {
                        sensitivity: "base",
                    })
                );
            });
        }

        function filterItems(key) {
            if (key && key in showFilter) showFilter[key] = false;

            filteredData.value = operationsList.value.filter((item) =>
                filterKeys.every((filterKey) => {
                    const filterValue = selectedFilters[filterKey];
                    const itemValue =
                        item[filterKey] ?? item.job_asmbl?.[filterKey];
                    return !filterValue || itemValue === filterValue;
                })
            );
        }

        // --- WATCH OPERATIONS LIST ---
        watch(
            operationsList,
            () => {
                setFilterOptions();
                filterItems();
            },
            { immediate: true }
        );

        // --- SCRAP MODAL FUNCTIONS ---
        function openScrapModel(item) {
            editedOpr.value = JSON.parse(JSON.stringify(item));
            origOpr.value = JSON.parse(JSON.stringify(item));
            applyToAll.value = false;
            editScrapModal.value = true;
        }
        function closeScrapModel() {
            editScrapModal.value = false;
        }

        async function submit() {
            loading.value = true;
            const scrapChanged =
                Number(editedOpr.value.EstScrap) !==
                Number(origOpr.value.EstScrap);
            const typeChanged =
                editedOpr.value.EstScrapType !== origOpr.value.EstScrapType;
            loading.value = false;

            if (!scrapChanged && !typeChanged && !applyToAll.value) {
                loading.value = false;
                window.alert(
                    "No Changes Were Made. Please Update Either Qty, Type, Or Apply To All."
                );
                return;
            }

            await sendUpdateCall({
                type: "EstScrapBundle",
                EstScrap: editedOpr.value.EstScrap,
                EstScrapType: editedOpr.value.EstScrapType,
            });

            editScrapModal.value = false;
        }

        async function estScrapChange() {
            if (
                Number(editedOpr.value.EstScrap) !==
                Number(origOpr.value.EstScrap)
            ) {
                await sendUpdateCall({
                    type: "EstScrap",
                    EstScrap: editedOpr.value.EstScrap,
                    EstScrapType: editedOpr.value.EstScrapType,
                });
                return true;
            }
            return false;
        }

        async function estScrapTypeChange() {
            if (editedOpr.value.EstScrapType !== origOpr.value.EstScrapType) {
                await sendUpdateCall({
                    type: "EstScrapType",
                    EstScrap: editedOpr.value.EstScrap,
                    EstScrapType: editedOpr.value.EstScrapType,
                });
                return true;
            }
            return false;
        }

        async function sendUpdateCall(additionalParams) {
            if (
                editedOpr.value &&
                job.value.JobNum &&
                parseInt(job.value.JobClosed) === 0
            ) {
                const params = {
                    OpCode: editedOpr.value.OpCode,
                    OperationId: origOpr.value.id,
                    OprSeq: origOpr.value.OprSeq,
                    AssemblySeq: origOpr.value.AssemblySeq,
                    JobNum: origOpr.value.JobNum,
                    ApplyToAll: applyToAll.value,
                    ...additionalParams,
                };
                try {
                    const response = await axios.post(
                        route("api.epicor.jobs.opr.update"),
                        params
                    );
                    if (response.data.Status === 0)
                        setErrorMessage(response.data.Message);
                    else if (response.data.Status === 1) {
                        refreshAssemblies("operation");
                        setTemporaryMessage("Value has been updated and saved");
                    }
                } catch (err) {
                    console.log("Err:", err);
                }
            }
        }

        return {
            job,
            tableHeaders,
            operationsList,
            filteredData,
            filterOptions,
            selectedFilters,
            showFilter,
            hoverColumn,
            editScrapModal,
            loading,
            editedOpr,
            origOpr,
            applyToAll,
            openScrapModel,
            closeScrapModel,
            estScrapChange,
            estScrapTypeChange,
            sendUpdateCall,
            submit,
            filterItems,
            setFilterOptions,
            refreshAssemblies,
            setErrorMessage,
            setTemporaryMessage,
        };
    },
};
</script>

<style scoped></style>
